// Canvas overlay for live code highlighting.
// Renders a transparent <canvas> on top of the CodeMirror editor and draws
// per-character visual effects — color tints, glows — driven by the
// highlighter at ~20 fps. No CSS outlines, no boxes.
//
// Wire:  canvas = new CodeCanvas(editor.view)
//        canvas.tick(ranges)  — called from the audio loop

export class CodeCanvas {
	constructor(view) {
		this.view = view;
		this._canvas = null;
		this._ctx = null;
		this._raf = 0;
		this._ranges = [];
		this._enabled = true;
		this._ready = false;
		this._stripePat = null; // diagonal stripe pattern for structural highlights

		requestAnimationFrame(() => this._create());
	}

	_fillStripeRect(ctx, x, y, w, h, color) {
		// Draw diagonal stripes directly — more reliable than createPattern
		// with transparent holes, which depends on compositing context.
		ctx.save();
		roundRect(ctx, x, y, w, h, 3);
		ctx.clip();
		ctx.fillStyle = color;
		ctx.fillRect(x, y, w, h);
		// Overdraw diagonal lines in the background color (effectively erasing stripes)
		ctx.fillStyle = 'rgba(0,0,0,0.35)';
		const step = 6;
		for (let i = -h; i < w + h; i += step) {
			ctx.beginPath();
			ctx.moveTo(x + i, y);
			ctx.lineTo(x + i - h, y + h);
			ctx.lineWidth = 2.5;
			ctx.strokeStyle = 'rgba(0,0,0,0.35)';
			ctx.stroke();
		}
		ctx.restore();
	}

	set enabled(v) {
		this._enabled = v;
		if (!v && this._ctx) {
			this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
		}
	}

	tick(ranges) {
		if (!this._enabled || !this._ready) return;
		this._ranges = ranges || [];
		if (!this._raf) this._startLoop();
	}

	destroy() {
		cancelAnimationFrame(this._raf);
		if (this._resizeObs) this._resizeObs.disconnect();
		if (this._canvas) this._canvas.remove();
		this._canvas = null; this._ctx = null;
	}

	// ── Internals ─────────────────────────────────────────────────────

	_create() {
		const dom = this.view.dom;
		if (!dom || !dom.parentElement) return;

		this._canvas = document.createElement('canvas');
		this._canvas.id = 'coderadio-code-canvas';
		// Sit above editor content, below cursor/selection. pointer-events:none
		// so all mouse interaction falls through to CodeMirror.
		this._canvas.style.cssText =
			'position:absolute;top:0;left:0;z-index:1;pointer-events:none;opacity:0.45;';
		this._canvas.setAttribute('aria-hidden', 'true');

		// Insert as first child of the editor's positioned container
		dom.style.position = 'relative';
		dom.appendChild(this._canvas);

		this._ctx = this._canvas.getContext('2d');
		this._syncSize();
		this._ready = true;

		// Keep size in sync with editor layout changes
		this._resizeObs = new ResizeObserver(() => this._syncSize());
		this._resizeObs.observe(dom);
	}

	_syncSize() {
		if (!this._canvas || !this._ctx) return;
		const dpr = window.devicePixelRatio || 1;
		const rect = this.view.dom.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0) return;

		this._canvas.style.width  = rect.width  + 'px';
		this._canvas.style.height = rect.height + 'px';
		this._canvas.width  = Math.round(rect.width  * dpr);
		this._canvas.height = Math.round(rect.height * dpr);
		this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		this._editorRect = rect;
	}

	_startLoop() {
		const loop = () => {
			this._raf = 0;
			this._render();
			// Keep looping while we have ranges OR recently had them
			// (one extra frame to clear after ranges go empty)
			if (this._ranges.length || this._hadRanges) {
				this._hadRanges = this._ranges.length > 0;
				this._raf = requestAnimationFrame(loop);
			}
		};
		this._hadRanges = false;
		this._raf = requestAnimationFrame(loop);
	}

	_render() {
		const ctx = this._ctx;
		if (!ctx || !this._editorRect) return;
		const cw = this._canvas.width / (window.devicePixelRatio || 1);
		const ch = this._canvas.height / (window.devicePixelRatio || 1);
		ctx.clearRect(0, 0, cw, ch);

		const edLeft = this._editorRect.left;
		const edTop  = this._editorRect.top;

		for (const r of this._ranges) {
			const start = this.view.coordsAtPos(r.from);
			const end   = this.view.coordsAtPos(r.to);
			if (!start || !end) continue;

			const x = start.left - edLeft;
			const y = start.top  - edTop;
			const w = end.right  - start.left;
			const h = Math.max(end.bottom - start.top, start.bottom - start.top);
			if (w <= 0 || h <= 0) continue;

			if (r.render === 'stripe') {
				// Structural highlight — diagonal stripes + border
				this._fillStripeRect(ctx, x - 1, y, w + 2, h,
					colorToRGBA(r.color || '#4ec05a', 0.22));
				ctx.strokeStyle = colorToRGBA(r.color || '#4ec05a', 0.50);
				ctx.lineWidth = 1;
				roundRect(ctx, x - 1, y, w + 2, h, 3);
				ctx.stroke();
			} else if (r.render === 'pulse') {
				// Bit-shift pulse — subtle gold dot, rhythmic
				ctx.fillStyle = colorToRGBA(r.color || '#ffd28a', 0.20);
				roundRect(ctx, x, y, w, h, 3);
				ctx.fill();
			} else {
				// Default / glow — flat fill, operator / number tint
				ctx.fillStyle = colorToRGBA(r.color || '#4ec05a', r.alpha != null ? r.alpha : 0.15);
				roundRect(ctx, x - 1, y, w + 2, h, 3);
				ctx.fill();
			}
		}
	}
}

function colorToRGBA(color, alpha) {
	if (color.startsWith('#')) {
		const r = parseInt(color.slice(1, 3), 16);
		const g = parseInt(color.slice(3, 5), 16);
		const b = parseInt(color.slice(5, 7), 16);
		return `rgba(${r},${g},${b},${alpha})`;
	}
	return color;
}

function roundRect(ctx, x, y, w, h, r) {
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.lineTo(x + w - r, y);
	ctx.quadraticCurveTo(x + w, y, x + w, y + r);
	ctx.lineTo(x + w, y + h - r);
	ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
	ctx.lineTo(x + r, y + h);
	ctx.quadraticCurveTo(x, y + h, x, y + h - r);
	ctx.lineTo(x, y + r);
	ctx.quadraticCurveTo(x, y, x + r, y);
	ctx.closePath();
}
